/**
 * MER-582: which source carries division, and under what column name.
 *
 * ONE source of truth, imported by both the runtime (globalFilters.ts) and the
 * static audit harness (scripts/audit-filters.mjs). They used to keep separate
 * hand-maintained lists, and both were wrong in opposite directions:
 *
 *   - the runtime's DIVISION_UNAWARE_SOURCES held a single entry
 *     ("Meridian - Audit Findings") with an in-code comment asserting "today
 *     only audit_finding lacks the column". That was wrong for three tables and
 *     three CQT views.
 *   - the harness's DIVISION_AWARE_SOURCES named five sources that appear in no
 *     manifest at all ("Meridian - SERVICE_EVENT", "Meridian - INVOICE",
 *     "Meridian - ROUTE", "Meridian - ASSET", "Meridian - CUSTOMER" -- canonical
 *     ENTITY names, not manifest display names), and omitted every source that
 *     is genuinely division-aware. So it reported a clean bill of health while
 *     four live tiles blanked on any division selection.
 *
 * Every entry below was read out of dashboard/supabase/migrations, not assumed.
 *
 * Plain .mjs so both a TS module and node:test can import it with no build step.
 */

/**
 * Sources with NO division column at all. Injecting a division predicate makes
 * PostgREST 42703, and the dashboard renders its "awaiting source data" gate
 * over rows that are perfectly fine.
 *
 * view_cqt_14 / 15 / 16 group by billing_cycle / ar_type / period and project
 * no division. view_cqt_23 DOES project one and is deliberately absent here.
 */
export const DIVISION_UNAWARE_SOURCES = Object.freeze([
  "Meridian - Audit Findings",
  "CQT-14",
  "CQT-15",
  "CQT-16",
]);

/**
 * Sources whose division column is not called "division". These carry real
 * division data as `division_name`; the fix is an alias so the filter WORKS,
 * not a suppression that quietly switches it off.
 */
export const DIVISION_COLUMN_BY_SOURCE = Object.freeze({
  "Meridian - AR Current": "division_name",
  "Meridian - AR Transactions": "division_name",
  "Meridian - Site Contracts": "division_name",
  // MER-551: public.disposal keeps the export's own column names, and the
  // export calls it "Division Name". It also carries a division_id, which is
  // a Navusoft surrogate and not what a division filter selects on.
  "Meridian - Disposal": "division_name",
});

/** Sources that carry division under some column. Derived, not listed twice. */
export const DIVISION_AWARE_SOURCES = Object.freeze([
  "Meridian - KPI Summary",
  "Meridian - Financial Health",
  "Meridian - Service Events",
  "Meridian - Route KPIs",
  "Meridian - Assets",
  "Meridian - Customers",
  "Meridian - Top AR Accounts",
  "Meridian - Contract Expirations",
  "Meridian - AR Current",
  "Meridian - AR Transactions",
  "Meridian - Site Contracts",
  "Meridian - Disposal",
  "CQT-23",
]);

/** The column a source stores its division in. */
export function divisionColumnFor(sourceName) {
  if (!sourceName) return "division";
  return DIVISION_COLUMN_BY_SOURCE[sourceName] ?? "division";
}

/** True when a division predicate would 42703 on this source. */
export function isDivisionUnaware(sourceName) {
  return Boolean(sourceName) && DIVISION_UNAWARE_SOURCES.includes(sourceName);
}
